import { Component, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { VbSnackbarService } from '@viewbid/ui';
import {
    AuctionCatalogsService,
    GetExistingLotNumber200Response,
    GetLotById200Response,
} from '@viewbid/ts-angular-client';
import {
    Observable,
    Subject,
    catchError,
    finalize,
    forkJoin,
    map,
    of,
    switchMap,
    take,
    takeUntil,
} from 'rxjs';
import { TokenStorageService, User } from 'viewbid-shared-lib';
import { isAdminUser, selectUser } from 'src/app/viewbid/state/app-selectors';
import { AppState } from 'src/app/viewbid/state/app-state';
import { ServerClockService } from 'src/app/viewbid/shared/services/server-clock.service';
import { canViewClosedLot } from 'src/app/viewbid/shared/utils/closed-lot-access.util';
import {
    LotResponseWithProxy,
    normalizeLotRow,
} from 'src/app/viewbid/shared/utils/lot-card-mapper.util';
import { hasTimePassed } from 'src/app/viewbid/shared/utils/lot-countdown.util';

type LotJumpVerdict =
    | { kind: 'viewable'; lotId: string }
    | { kind: 'closed' }
    | { kind: 'not-found' };

@Component({
    selector: 'vb-lot-jump',
    templateUrl: './lot-jump.component.html',
    styleUrls: ['./lot-jump.component.scss'],
})
export class LotJumpComponent implements OnDestroy {
    @Input() auctionId!: string;

    public lotNumberJump: string = '';
    public isJumpingToLot: boolean = false;

    private readonly destroy$: Subject<void> = new Subject<void>();

    constructor(
        private readonly auctionService: AuctionCatalogsService,
        private readonly router: Router,
        private readonly snackbarService: VbSnackbarService,
        private readonly translate: TranslateService,
        private readonly store: Store<AppState>,
        private readonly serverClock: ServerClockService,
        private readonly tokenStorageService: TokenStorageService,
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    jumpToLot(): void {
        const lotNumber: string = this.lotNumberJump.trim();
        if (!lotNumber || !this.auctionId || this.isJumpingToLot) {
            return;
        }

        this.isJumpingToLot = true;
        this.auctionService
            .getExistingLotNumber(this.auctionId, lotNumber)
            .pipe(
                switchMap((response: GetExistingLotNumber200Response) => {
                    const lotId: string | undefined = response.data?.lotId;
                    return lotId
                        ? this.judgeJumpTarget(lotId)
                        : of<LotJumpVerdict>({ kind: 'not-found' });
                }),
                catchError(() => of<LotJumpVerdict>({ kind: 'not-found' })),
                finalize(() => {
                    this.isJumpingToLot = false;
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((verdict: LotJumpVerdict) =>
                this.followVerdict(verdict, lotNumber),
            );
    }

    private judgeJumpTarget(lotId: string): Observable<LotJumpVerdict> {
        const viewable: LotJumpVerdict = { kind: 'viewable', lotId };
        return forkJoin([
            this.auctionService.getLotById(lotId),
            this.store.select(selectUser).pipe(take(1)),
        ]).pipe(
            map(
                ([response, user]: [
                    GetLotById200Response,
                    User | null,
                ]): LotJumpVerdict =>
                    this.isKnownClosedToViewer(
                        normalizeLotRow(response.data),
                        user,
                    )
                        ? { kind: 'closed' }
                        : viewable,
            ),
            catchError(() => of(viewable)),
        );
    }

    private isKnownClosedToViewer(
        lot: LotResponseWithProxy,
        user: User | null,
    ): boolean {
        if (
            !this.serverClock.canJudgePassed(lot.toDatetime) ||
            !hasTimePassed(lot.toDatetime, this.serverClock.now())
        ) {
            return false;
        }
        if (!user && this.tokenStorageService.getToken()) {
            return false;
        }
        return !canViewClosedLot(lot, {
            id: user?.accountMappingsId,
            isAdmin: isAdminUser(user),
        });
    }

    private followVerdict(verdict: LotJumpVerdict, lotNumber: string): void {
        switch (verdict.kind) {
            case 'viewable':
                this.lotNumberJump = '';
                this.router.navigate([
                    '/auctions',
                    this.auctionId,
                    'lot',
                    verdict.lotId,
                ]);
                return;
            case 'closed':
                this.snackbarService.error(
                    this.translate.instant('LotDetails.closedLotUnavailable'),
                );
                return;
            case 'not-found':
                this.snackbarService.error(
                    this.translate.instant('AuctionsPage.jumpToLotNotFound', {
                        number: lotNumber,
                    }),
                );
        }
    }
}
